/**
 * The working-time record's constant values (docs/OSEBJE.md §8, §9), mirroring
 * `pkg/enums/*` on the API side.
 *
 * Written out here rather than read off the wire for the reason `enums/staff.ts`
 * already gives: every one of them needs a Slovenian word beside it, and a label
 * assembled from a wire name is a key `yarn check-i18n` cannot see.
 */

/**
 * What one entry of the record says happened. There is deliberately no "day" — a day is
 * calculated from these when somebody reads it, and nowhere stored (§3).
 *
 * The first four are the presses. `Corrected` is not pressed by anybody: it is the
 * entry an APPROVED correction appends (§9), and its own `kind` says only that — what
 * the record now reads is in `corrected_kind`. Anything that folds these into a state
 * has to resolve it, which `effectiveKind` in `libs/staffTime.ts` does; switching on
 * `kind` alone would silently drop every corrected arrival out of the day.
 */
export enum StaffTimeEventKind {
    ClockIn    = 'clock_in',
    ClockOut   = 'clock_out',
    BreakStart = 'break_start',
    BreakEnd   = 'break_end',
    Corrected  = 'corrected_time_event',
}

/**
 * Where a stamp came from. It is on the record because a shared terminal stays
 * shareable however good the PIN is (§8): one person can stamp another, no software
 * prevents it, and the honest answer is to write down which machine it came from and
 * let a manager see it.
 */
export enum StaffTimeSource {
    /** The person's own telephone or browser — the strongest evidence there is here. */
    PersonalDevice = 'personal_device',

    /** The tablet in the corridor, with a personal PIN. */
    Kiosk = 'kiosk',

    /** Written in afterwards through an approved correction (§9). */
    Manual = 'manual',
}

/**
 * Where a correction has got to (§9). Requested and decided are separate states
 * because "what somebody proposed" and "what became the record" are the whole reason
 * this trail is worth anything in a dispute.
 */
export enum StaffTimeCorrectionState {
    Requested = 'requested',
    Approved  = 'approved',
    Rejected  = 'rejected',
}

/**
 * What has happened to time worked beyond the shift (§8, "evidentiraj vedno, razvrsti
 * pozneje").
 *
 * It is a STATE and not a label: the minutes are in the record and in the balance
 * whatever it says, and all this decides is whether somebody still has to go and look
 * at them.
 *
 * There are exactly two values, because classifying an excess is phase 3 and until it
 * exists there is nothing for a day to be classified AS. `None` is the empty string the
 * API actually answers — not the word "none", which nothing writes.
 */
export enum StaffTimeExcessState {
    None                  = '',
    AwaitingClassification = 'awaiting_classification',
}
